/**
 * LiveConversations — Superadmin Command Center Widget
 *
 * Fixes:
 *  1. Messages from different visitors were mixing into one chat box.
 *     Root cause: state was a flat array shared across all sessions.
 *     Fix: messages stored as { [socket_id]: [...messages] } — each
 *          visitor gets their own isolated thread.
 *
 *  2. Admin reply now emits to the correct visitor's socket_id only.
 *
 * Usage in CommandCenter/SuperAdminDashboard:
 *   import LiveConversations from './LiveConversations';
 *   <LiveConversations socket={socket} />
 *
 * Props:
 *   socket — the socket.io client instance from the parent
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge, Form, Button, Spinner } from 'react-bootstrap';
import { Send, Bell, Users, MessageCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const fmtTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });
};

const QUICK_REPLIES = [
    'Hi! How can I help you today?',
    'Thank you for reaching out. Let me check on that.',
    'Could you please share more details?',
    'Our team will get back to you within 24 hours.',
    'Is there anything else I can help you with?',
];

const LiveConversations = ({ socket }) => {
    // ── State ──────────────────────────────────────────────────────────────
    const [sessions,       setSessions]       = useState({}); // { socket_id: { lastMsg, unread, lastTime } }
    const [selectedId,     setSelectedId]     = useState(null);
    // FIX 1: messages keyed by socket_id → completely isolated per visitor
    const [messagesBySession, setMsgBySession] = useState({}); // { socket_id: [{...}] }
    const [input,          setInput]           = useState('');
    const [sending,        setSending]         = useState(false);
    const [loading,        setLoading]         = useState(false);
    const [showQuick,      setShowQuick]       = useState(false);
    const [totalUnread,    setTotalUnread]      = useState(0);
    const bottomRef = useRef(null);

    // ── Auto-scroll when messages change for selected session ──────────────
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messagesBySession, selectedId]);

    // ── Load existing sessions from DB on mount ────────────────────────────
    useEffect(() => {
        axiosInstance.get('/superadmin/chat/sessions')
            .then(r => {
                const data = r.data.data || [];
                const sessMap = {};
                data.forEach(s => {
                    sessMap[s.socket_id] = {
                        socket_id:    s.socket_id,
                        lastMsg:      s.last_message || '',
                        unread:       parseInt(s.unread_count) || 0,
                        lastTime:     s.last_message_at,
                        messageCount: parseInt(s.message_count) || 0,
                    };
                });
                setSessions(sessMap);
                // Total unread badge
                const total = data.reduce((sum, s) => sum + (parseInt(s.unread_count)||0), 0);
                setTotalUnread(total);
            })
            .catch(() => {});
    }, []);

    // ── Load messages for a session when selected ──────────────────────────
    const loadMessages = useCallback(async (socketId) => {
        if (!socketId) return;
        // Already loaded → don't reload
        if (messagesBySession[socketId]) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get('/superadmin/chat/messages',
                { params: { socket_id: socketId, limit: 100 } }
            );
            const msgs = (res.data.data || []).map(m => ({
                id:          m.id,
                socket_id:   socketId,
                sender:      m.sender_type, // 'visitor' | 'admin'
                text:        m.message,
                timestamp:   m.created_at,
            }));
            setMsgBySession(prev => ({ ...prev, [socketId]: msgs }));
            // Mark as read
            axiosInstance.patch('/superadmin/chat/messages/read',
                { socket_id: socketId }).catch(() => {});
            setSessions(prev => ({
                ...prev,
                [socketId]: { ...prev[socketId], unread: 0 },
            }));
            setTotalUnread(prev => Math.max(0, prev - (sessions[socketId]?.unread || 0)));
        } catch {
            toast.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    }, [messagesBySession, sessions]);

    const selectSession = (socketId) => {
        setSelectedId(socketId);
        setInput('');
        setShowQuick(false);
        loadMessages(socketId);
    };

    // ── Socket.io — incoming visitor message ───────────────────────────────
    useEffect(() => {
        if (!socket) return;

        const handleVisitorMessage = (data) => {
            const { socket_id, message, timestamp } = data;

            // FIX 1: append to the correct session's message array only
            const newMsg = {
                id:        Date.now(),
                socket_id,
                sender:    'visitor',
                text:      message,
                timestamp: timestamp || new Date().toISOString(),
            };

            setMsgBySession(prev => ({
                ...prev,
                [socket_id]: [...(prev[socket_id] || []), newMsg],
            }));

            // Update session metadata
            setSessions(prev => ({
                ...prev,
                [socket_id]: {
                    ...prev[socket_id],
                    socket_id,
                    lastMsg:  message,
                    lastTime: timestamp || new Date().toISOString(),
                    unread:   selectedId === socket_id
                        ? 0
                        : ((prev[socket_id]?.unread || 0) + 1),
                },
            }));

            // Total unread count
            if (selectedId !== socket_id) {
                setTotalUnread(prev => prev + 1);
            }

            // Toast if not currently viewing this session
            if (selectedId !== socket_id) {
                const shortId = socket_id.slice(-5);
                toast.info(`Visitor ${shortId}: ${message.slice(0, 50)}${message.length > 50 ? '…' : ''}`, {
                    position: 'bottom-right',
                    onClick: () => selectSession(socket_id),
                });
            }
        };

        socket.on('admin_receive_message', handleVisitorMessage);
        return () => socket.off('admin_receive_message', handleVisitorMessage);
    }, [socket, selectedId]);

    // ── Admin send reply ───────────────────────────────────────────────────
    const sendReply = async (text) => {
        const msg = (text || input).trim();
        if (!msg || !selectedId || sending) return;
        setSending(true);
        setInput('');
        setShowQuick(false);

        // Optimistic UI — add to this session's messages immediately
        const optimistic = {
            id:        `opt_${Date.now()}`,
            socket_id: selectedId,
            sender:    'admin',
            text:      msg,
            timestamp: new Date().toISOString(),
        };
        setMsgBySession(prev => ({
            ...prev,
            [selectedId]: [...(prev[selectedId] || []), optimistic],
        }));

        try {
            // FIX 2: emit with the selected visitor's socket_id
            socket.emit('admin_message', {
                visitorSocketId: selectedId,
                message:         msg,
            });
            // Also persist via REST (belt-and-suspenders)
            await axiosInstance.post('/superadmin/chat/messages', {
                socket_id: selectedId,
                message:   msg,
            });
        } catch {
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendReply();
        }
    };

    // ── Sorted sessions list ───────────────────────────────────────────────
    const sessionList = Object.values(sessions).sort((a, b) =>
        new Date(b.lastTime || 0) - new Date(a.lastTime || 0)
    );

    const selectedMessages = selectedId ? (messagesBySession[selectedId] || []) : [];
    const selectedSession  = selectedId ? sessions[selectedId] : null;
    const shortId = (id) => `Visitor ${id?.slice(-5) || ''}`;

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div style={{
            display: 'flex', height: 620,
            border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden',
            background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
            {/* ── Left: session list ── */}
            <div style={{
                width: 260, borderRight: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column', flexShrink: 0,
            }}>
                {/* Header */}
                <div style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#f8fafc',
                }}>
                    <div className="d-flex align-items-center gap-2">
                        <MessageCircle size={16} className="text-primary"/>
                        <span className="fw-bold small">Live Conversations</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {totalUnread > 0 && (
                            <Badge bg="danger" style={{ fontSize: 10 }}>{totalUnread}</Badge>
                        )}
                        <Bell size={14} className="text-muted"/>
                    </div>
                </div>

                {/* Session list */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {sessionList.length === 0 ? (
                        <div className="text-center text-muted p-4" style={{ fontSize: 13 }}>
                            <Users size={28} className="mb-2 opacity-50 d-block mx-auto"/>
                            No conversations yet.<br/>
                            Visitors who use Live Support<br/>will appear here.
                        </div>
                    ) : (
                        sessionList.map(sess => (
                            <div
                                key={sess.socket_id}
                                onClick={() => selectSession(sess.socket_id)}
                                style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f1f5f9',
                                    background: selectedId === sess.socket_id
                                        ? '#eff6ff' : '#fff',
                                    borderLeft: `3px solid ${selectedId === sess.socket_id ? '#3b82f6' : 'transparent'}`,
                                    transition: 'background 0.15s',
                                }}>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="d-flex align-items-center gap-2">
                                        {/* Avatar */}
                                        <div style={{
                                            width: 34, height: 34, borderRadius: '50%',
                                            background: '#dbeafe', color: '#1d4ed8',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 13, fontWeight: 700, flexShrink: 0,
                                        }}>
                                            {sess.socket_id.slice(-2).toUpperCase()}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>
                                                {shortId(sess.socket_id)}
                                            </div>
                                            <div className="text-muted text-truncate"
                                                style={{ fontSize: 11, maxWidth: 140 }}>
                                                {sess.lastMsg || 'No messages yet'}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                        {sess.unread > 0 && (
                                            <Badge bg="primary" style={{ fontSize: 9 }}>
                                                {sess.unread}
                                            </Badge>
                                        )}
                                        <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
                                            {fmtTime(sess.lastTime)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* ── Right: message thread ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {!selectedId ? (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                        <MessageCircle size={40} className="mb-3 opacity-30"/>
                        <p className="small">Select a conversation to view messages</p>
                    </div>
                ) : (
                    <>
                        {/* Thread header */}
                        <div style={{
                            padding: '12px 20px',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: '#f8fafc',
                        }}>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }}/>
                                <span className="fw-bold small">
                                    Chatting with {selectedId.slice(-7)}
                                </span>
                            </div>
                            {/* Quick Reply dropdown */}
                            <div className="position-relative">
                                <Button size="sm" variant="outline-warning"
                                    style={{ fontSize: 12 }}
                                    onClick={() => setShowQuick(p => !p)}>
                                    ⚡ Quick Reply ▾
                                </Button>
                                {showQuick && (
                                    <div style={{
                                        position: 'absolute', right: 0, top: '110%',
                                        background: '#fff', border: '1px solid #e5e7eb',
                                        borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        zIndex: 100, minWidth: 280,
                                    }}>
                                        {QUICK_REPLIES.map((qr, i) => (
                                            <div key={i}
                                                onClick={() => sendReply(qr)}
                                                style={{
                                                    padding: '10px 14px', cursor: 'pointer',
                                                    fontSize: 13, borderBottom: i < QUICK_REPLIES.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                                {qr}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Messages — ISOLATED to selectedId only */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                            {loading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" size="sm" variant="primary"/>
                                </div>
                            ) : selectedMessages.length === 0 ? (
                                <div className="text-center text-muted" style={{ fontSize: 13, marginTop: 40 }}>
                                    No messages yet in this conversation.
                                </div>
                            ) : (
                                selectedMessages.map((msg, i) => {
                                    const isAdmin = msg.sender === 'admin';
                                    return (
                                        <div key={msg.id || i}
                                            className={`d-flex mb-3 ${isAdmin ? 'justify-content-end' : 'justify-content-start'}`}>
                                            <div style={{
                                                maxWidth: '70%',
                                                background: isAdmin ? '#3b82f6' : '#f1f5f9',
                                                color:      isAdmin ? '#fff'    : '#1e293b',
                                                borderRadius: isAdmin
                                                    ? '18px 18px 4px 18px'
                                                    : '18px 18px 18px 4px',
                                                padding: '10px 14px',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                wordBreak: 'break-word',
                                            }}>
                                                <div>{msg.text}</div>
                                                <div style={{
                                                    fontSize: 10, marginTop: 4,
                                                    opacity: 0.7, textAlign: isAdmin ? 'right' : 'left',
                                                }}>
                                                    {isAdmin ? 'You' : shortId(selectedId)} · {fmtTime(msg.timestamp)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={bottomRef}/>
                        </div>

                        {/* Input */}
                        <div style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #e5e7eb',
                            background: '#fff',
                        }}>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    as="textarea" rows={1}
                                    placeholder="Write your response..."
                                    value={input}
                                    onChange={e => setInput(e.target.value)}
                                    onKeyDown={onKeyDown}
                                    disabled={sending}
                                    style={{
                                        resize: 'none', borderRadius: 10,
                                        fontSize: 13, padding: '10px 14px',
                                        maxHeight: 80, overflowY: 'auto',
                                    }}
                                />
                                <Button
                                    variant="primary"
                                    style={{ borderRadius: 10, width: 44, padding: 0, flexShrink: 0 }}
                                    disabled={!input.trim() || sending}
                                    onClick={() => sendReply()}>
                                    {sending
                                        ? <Spinner size="sm" animation="border"/>
                                        : <Send size={16}/>}
                                </Button>
                            </div>
                            <div className="text-muted mt-1" style={{ fontSize: 10 }}>
                                Press Enter to send · Shift+Enter for new line
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LiveConversations;
